// Temporarily disabled - redirects to home page
// Original code backed up in lip.js.backup
import { useEffect } from "react";
import { useRouter } from "next/router";

export default function LIP() {
    const router = useRouter();

    useEffect(() => {
        router.replace("/");
    }, [router]);

    return null;
}

/* Original code preserved below - see lip.js.backup for full restoration
// src/pages/LIP.jsx
import {
    Autocomplete,
    Box,
    Button,
    Container,
    Divider,
    Paper,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";
import Page from "../component/Page";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { findClosetSiteWithLatLon } from "../utils/location-calculate";
import Head from "next/head";
import zipsLookup from "../utils/ky-zips"; // { "42788": {lat,lon} | {INTPTLAT,INTPTLONG}, ... }

// ---------------- constants ----------------
const DATA_BASE_URL = `https://d266k7wxhw6o23.cloudfront.net/daily`;
const CDN_ROOT = DATA_BASE_URL.replace(/\/daily\/?$/, "");
const absFromKey = (key) => `${CDN_ROOT}/${String(key).replace(/^\//, "")}`;

// ---------------- remote file discovery ----------------
async function resolveStationYearUrls(stationId, year) {
    const stationRoot = `${DATA_BASE_URL}/${stationId}`;
    try {
        const manRes = await fetch(`${stationRoot}/manifest.json`, { cache: "no-store" });
        if (!manRes.ok) return [];
        const manifest = await manRes.json();
        const direct = manifest?.[`${year}_L`];
        if (!direct?.key) return [];
        return [absFromKey(direct.key)];
    } catch {
        return [];
    }
}

// ---------------- data shaping for daily view ----------------
async function buildDailyFromJson(urls, stationId = "FARM") {
    const toNum = (v) => {
        if (typeof v === "number") return Number.isFinite(v) ? v : null;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
    };

    const datasets = await Promise.all(
        (urls || []).map(async (u) => {
            const res = await fetch(u, { cache: "no-store" });
            if (!res.ok) throw new Error(`Failed to fetch ${u}: ${res.status}`);
            return res.json();
        })
    );

    const needed = new Set(["TargetDate", "Variable", "Min_Val", "Max_Val", "Avg_Val"]);
    const byDate = new Map();

    for (const data of datasets) {
        if (data.station && stationId && data.station !== stationId) continue;
        const cols = data.columns || [];
        for (const n of needed) {
            if (!cols.includes(n)) throw new Error(`Missing expected column ${n}`);
        }
        const idx = Object.fromEntries(cols.map((c, i) => [c, i]));
        const keepVar = new Set(["TAIR", "RELH", "WCHI", "THI"]);

        for (const row of data.rows || []) {
            const variable = row[idx.Variable];
            if (!keepVar.has(variable)) continue;
            const date = row[idx.TargetDate];
            let entry = byDate.get(date);
            if (!entry) {
                entry = { TAIR: null, RELH: null, WCHI: null, THI: null };
                byDate.set(date, entry);
            }
            let min = toNum(row[idx.Min_Val]);
            let max = toNum(row[idx.Max_Val]);
            let avg = toNum(row[idx.Avg_Val]);

            // Convert TAIR/WCHI from C to F
            if (variable === "TAIR" || variable === "WCHI" || variable === "THI") {
                min = min != null ? (min * 9) / 5 + 32 : null;
                max = max != null ? (max * 9) / 5 + 32 : null;
                avg = avg != null ? (avg * 9) / 5 + 32 : null;
            }
            entry[variable] = { min, max, avg };
        }
    }

    const daily = [];
    for (const [date, vars] of Array.from(byDate.entries()).sort((a, b) => new Date(a[0]) - new Date(b[0]))) {
        const Temp_min = vars.TAIR?.min ?? null;
        const Windchill_min = vars.WCHI?.min ?? null;
        const THI_max = vars.THI?.max ?? null;
        daily.push({ date, THI_max, Temp_min, Windchill_min });
    }
    return daily;
}

// ---------------- UI helpers ----------------
const Color = {
    GRAY: "#bdbdbd"
};

function flagColor() {
    return Color.GRAY;
}

function TooltipLists({ daily, dateStr }) {
    const dayData = daily.find(d => d.date === dateStr);
    if (!dayData) return <Typography variant="body2">No data available</Typography>;

    const formatValue = (val) => {
        if (val == null || !Number.isFinite(val)) return "N/A";
        return val.toFixed(1);
    };

    return (
        <Box>
            <Typography variant="body2">Daily Minimum Temperature: {formatValue(dayData.Temp_min)}°F</Typography>
            <Typography variant="body2">Daily Minimum Wind Chill: {formatValue(dayData.Windchill_min)}°F</Typography>
            <Typography variant="body2">Daily Livestock Stress Index: {formatValue(dayData.THI_max)}</Typography>
        </Box>
    );
}

// ---------------- main page ----------------
export default function LIP() {
    // Gate anything time/window related to the client
    const [mounted, setMounted] = useState(false);

    // Controls
    const [stationId, setStationId] = useState("");
    const [options, setOptions] = useState([]);
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [viewMode, setViewMode] = useState("year");
    const [targetDate, setTargetDate] = useState("1970-01-01");

    // Data
    const [lipData, setLipData] = useState([]);
    const [daily, setDaily] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stations, setStations] = useState({});

    // URL writer gate
    const [hydrated, setHydrated] = useState(false);

    // Uncontrolled ZIP input
    const zipRef = useRef(null);

    const selectedOption = useMemo(
        () => (options && stationId ? options.find((o) => o.value === stationId) || null : null),
        [options, stationId]
    );

    const flagByDate = useMemo(() => {
        const m = new Map();
        for (const r of daily) m.set(r.date, r.flag);
        return m;
    }, [daily]);

    // After mount, read URL and set defaults
    useEffect(() => {
        moment.tz.setDefault("America/Chicago");
        const params = new URLSearchParams(window.location.search);
        const now = moment();

        const initialStationId = params.get("station") || "FARM";
        const initialYear = params.get("year") || String(now.year());
        const initialMonth = params.get("month") || now.format("MMMM");
        const initialMode = params.get("mode") || "year";

        setStationId(initialStationId);
        setYear(initialYear);
        setMonth(initialMonth);
        setViewMode(initialMode);
        setTargetDate(now.format("YYYY-MM-DD"));

        setMounted(true);
    }, []);

    // Load stations after mount
    useEffect(() => {
        if (!mounted) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch("/api/getStationsInfo");
                const json = await res.json();

                const list = Object.entries(json)
                    .map(([key, item]) => (!item.isDiscontinued ? { label: `${item.county} County (${key})`, value: key } : null))
                    .filter(Boolean)
                    .sort((a, b) => a.label.localeCompare(b.label));

                if (cancelled) return;
                setOptions(list);
                setStations(json);

                const hasStation = list.some((o) => o.value === (stationId || ""));
                if (!hasStation && list.length && !new URLSearchParams(window.location.search).get("station")) {
                    setStationId(list[0].value);
                }
            } finally {
                if (!cancelled) setHydrated(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mounted]);

    // Write URL only after hydrated
    useEffect(() => {
        if (!mounted || !hydrated) return;
        if (!stationId || !year) return;
        const params = new URLSearchParams();
        params.set("station", stationId);
        params.set("year", year);
        params.set("mode", viewMode);
        if (viewMode === "month" && month) params.set("month", month);
        const url = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", url);
    }, [mounted, hydrated, stationId, year, month, viewMode]);

    // Fetch data when selection changes
    useEffect(() => {
        if (!mounted || !stationId || !year) return;
        setLoading(true);
        (async () => {
            try {
                const urls = await resolveStationYearUrls(stationId, year);
                if (!urls || !urls.length) throw new Error(`No data URLs for ${stationId} ${year}`);
                const dailyRows = await buildDailyFromJson(urls, stationId);
                setDaily(dailyRows);

                if (viewMode === "month" && month) {
                    const m = moment(`${year}-${month}`, "YYYY-MMMM");
                    const daysInMonth = m.daysInMonth();
                    const mm = m.format("MM");
                    const arr = Array(daysInMonth).fill(null);
                    for (const row of dailyRows) {
                        if (row.date?.startsWith(`${year}-${mm}`)) {
                            const day = parseInt(row.date.slice(8, 10), 10);
                            if (day >= 1 && day <= daysInMonth) arr[day - 1] = row.flag;
                        }
                    }
                    setLipData(arr);

                    const td = moment(targetDate, "YYYY-MM-DD", true);
                    if (!td.isValid() || td.format("YYYY-MM") !== m.format("YYYY-MM")) {
                        setTargetDate(m.date(Math.min(15, daysInMonth)).format("YYYY-MM-DD"));
                    }
                } else {
                    setLipData([]);
                }
            } catch (err) {
                console.error(err);
                if (viewMode === "month" && month) {
                    const daysInMonth = moment(`${year}-${month}`, "YYYY-MMMM").daysInMonth();
                    setLipData(Array(daysInMonth).fill(null));
                } else {
                    setLipData([]);
                }
                setDaily([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [mounted, stationId, year, month, viewMode]);

    // Skeleton to avoid hydration mismatch
    if (!mounted) {
        return (
            <Page>
                <Head><title>Livestock Extreme Weather Calendar - Kentucky Climate Center</title></Head>
                <Container maxWidth="xl">
                    <Typography variant="h6" sx={{ py: 8 }}>Loading…</Typography>
                </Container>
            </Page>
        );
    }

    // Calendar renderer
    const renderMonthGrid = (y, monthIdx0) => {
        const m = moment({ year: Number(y), month: monthIdx0, day: 1 });
        const label = m.format("MMMM");
        const startDay = m.startOf("month").day();
        const daysInMonth = m.daysInMonth();
        const mm = m.format("MM");

        const getFlag = () => null; // All days are gray

        return (
            <Paper key={`${y}-${monthIdx0}`} sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    {label} {y}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                        <Box key={day} sx={{ fontWeight: "bold", textAlign: "center" }}>{day}</Box>
                    ))}
                    {Array.from({ length: startDay }).map((_, i) => <Box key={`sp-${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, idx) => {
                        const d = idx + 1;
                        const dateStr = m.clone().date(d).format("YYYY-MM-DD");
                        const flag = getFlag(d);

                        return (
                            <Tooltip
                                key={d}
                                arrow
                                placement="top"
                                enterDelay={150}
                                leaveDelay={0}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            p: 2,
                                            backgroundColor: "background.paper",
                                            color: "text.primary",
                                            boxShadow: 6,
                                            minWidth: 300,
                                            maxWidth: 400,
                                            fontSize: "0.95rem"
                                        }
                                    },
                                    arrow: { sx: { color: "background.paper" } }
                                }}
                                title={
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: "1rem" }}>
                                            {dateStr}
                                        </Typography>
                                        <TooltipLists daily={daily} dateStr={dateStr} />
                                    </Box>
                                }
                            >
                                <Box
                                    sx={{
                                        border: "1px solid #eee",
                                        borderRadius: 1,
                                        minHeight: viewMode === "month" ? 60 : 30,
                                        fontSize: viewMode === "month" ? "1rem" : "0.875rem",
                                        backgroundColor: flagColor(flag),
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center"
                                    }}
                                    onClick={() => setTargetDate(dateStr)}
                                >
                                    {d}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Paper>
        );
    };

    // ZIP and geo handlers
    function doFindByZip() {
        const raw = zipRef.current?.value || "";
        const match = raw.trim().match(/^(\d{5})/);
        const five = match ? match[1] : null;
        if (!five) {
            alert("Enter a 5-digit ZIP.");
            return;
        }
        const z = zipsLookup[five];
        const lat = z?.lat ?? z?.INTPTLAT;
        const lon = z?.lon ?? z?.INTPTLONG;
        if (typeof lat !== "number" || typeof lon !== "number") {
            alert("ZIP not found in Kentucky list.");
            return;
        }
        const withinMiles = 200;
        const closestKey = findClosetSiteWithLatLon(stations, lat, lon, withinMiles);
        if (!closestKey) {
            alert(`No station found within ${withinMiles} miles of ${five}.`);
            return;
        }
        setStationId(stations[closestKey].abbr || closestKey);
    }

    function doFindByGeo() {
        const withinMiles = 200;
        if (!("geolocation" in navigator)) {
            alert("Geolocation not supported in this browser.");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const closestKey = findClosetSiteWithLatLon(
                    stations,
                    pos.coords.latitude,
                    pos.coords.longitude,
                    withinMiles
                );
                if (!closestKey) {
                    alert(`No station found within ${withinMiles} miles of your location.`);
                    return;
                }
                setStationId(stations[closestKey].abbr || closestKey);
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    alert("Location access is blocked. Enable it in your browser settings and try again.");
                } else if (error.code === error.POSITION_UNAVAILABLE) {
                    alert("Location information is unavailable.");
                } else if (error.code === error.TIMEOUT) {
                    alert("Request timed out. Try again.");
                } else {
                    alert("An unknown error occurred.");
                }
            }
        );
    }

    return (
        <Page>
            <Head>
                <title>Livestock Extreme Weather Calendar - Kentucky Climate Center</title>
            </Head>
            <Container maxWidth="xl">
                <Typography variant="h3" sx={{ pt: 5 }}>
                    Livestock Extreme Weather Calendar
                </Typography>
                <Typography variant="h6" sx={{ py: 2 }}>
                    Select Station and Year
                </Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Autocomplete
                        options={options}
                        renderInput={(params) => <TextField {...params} label="Stations" />}
                        onChange={(_, v) => setStationId(v?.value || "")}
                        value={selectedOption}
                        disableClearable
                        sx={{ minWidth: 320, flex: 2 }}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                    />
                    <Autocomplete
                        options={Array.from({ length: new Date().getFullYear() - 2007 + 1 }, (_, i) => String(2007 + i)).reverse()}
                        renderInput={(params) => <TextField {...params} label="Year" />}
                        onChange={(_, v) => v && setYear(String(v))}
                        value={year || null}
                        disableClearable
                        sx={{ minWidth: 160, flex: 1 }}
                    />
                </Box>

                <Box sx={{ pt: 2, gap: 1, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                    <Button variant="contained" size="large" startIcon={<MyLocationIcon />} onClick={doFindByGeo}>
                        Find my nearest station
                    </Button>

                    <TextField
                        label="ZIP (KY)"
                        inputRef={zipRef}
                        inputProps={{ inputMode: "numeric", pattern: "\\d{5}(-\\d{4})?" }}
                        sx={{ flex: 1, minWidth: 120 }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                doFindByZip();
                            }
                        }}
                    />
                    <Button variant="outlined" onClick={doFindByZip} size="large">
                        Find by ZIP
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ py: 1 }}>
                    <ToggleButtonGroup
                        exclusive
                        value={viewMode}
                        onChange={(_, v) => v && setViewMode(v)}
                        size="small"
                        sx={{ mb: 3 }}
                    >
                        <ToggleButton value="year">Show: All months</ToggleButton>
                        <ToggleButton value="month">Show: Single month</ToggleButton>
                    </ToggleButtonGroup>

                    {viewMode === "month" && (
                        <Autocomplete
                            options={[
                                "January", "February", "March", "April", "May", "June",
                                "July", "August", "September", "October", "November", "December"
                            ].reverse()}
                            renderInput={(params) => <TextField {...params} label="Month" />}
                            onChange={(_, v) => v && setMonth(v)}
                            value={month || null}
                            disableClearable
                            sx={{ minWidth: 180, flex: 1 }}
                        />
                    )}
                </Box>

                <Paper sx={{ p: 3, mb: 5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h6">
                            {viewMode === "month"
                                ? `Monthly view ${month} ${year} at ${selectedOption?.label || stationId}`
                                : `Year view ${year} at ${selectedOption?.label || stationId}`}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 5, position: "relative" }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "rgba(255,255,255,0.7)",
                                    zIndex: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <Typography variant="h6" sx={{ color: "#888" }}>Loading...</Typography>
                            </Box>
                        )}

                        {viewMode === "month" ? (
                            <Box sx={{ opacity: loading ? 0.5 : 1 }}>
                                {renderMonthGrid(year, moment(`${year}-${month}`, "YYYY-MMMM").month())}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat( auto-fit, minmax(300px, 1fr) )",
                                    gap: 2,
                                    opacity: loading ? 0.5 : 1
                                }}
                            >
                                {Array.from({ length: 12 }).map((_, mi) => renderMonthGrid(year, mi))}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Page>
    );
}
    */